import React, { useState } from "react";
import CameraOCR, { OCRScanResponse } from "./CameraOCR";

// Extend what we expect back from the Flask /scan endpoint
type CaptureFeedback = {
  ok?: boolean;
  message?: string;
  suggestions?: string[];
  overlay?: {
    angle_degrees?: number;
    area_ratio?: number;
    blur_score?: number;
    mean_brightness?: number;
    glare_fraction?: number;
    side_hint?: string;
    center_offset?: { x: number; y: number };
    quad?: number[][] | null;
  };
};

type StructuredFields = {
  _source?: "ocr" | "barcode" | "mrz";
  license_number?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  dob?: string;      // ISO yyyy-mm-dd
  expiry?: string;   // ISO
  issued?: string;   // ISO
  address_line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  sex?: string;
  height_str?: string;
  height_in?: string;
  eyes?: string;
  doc_number?: string; // from your generic parser
  // allow any extra keys without breaking
  [k: string]: any;
};

type ApiScanResponse = {
  ok?: boolean;
  notice?: string | null;
  error?: string | null;
  reasons?: string[];
  capture_feedback?: CaptureFeedback;
  structured?: StructuredFields;
  barcode?: any | null;
  mrz?: any | null;

  // Existing payload bits you were using:
  ocr_text?: string;
  preview_base64?: string;
  ocr_tokens?: any[];
  ocr_view_size?: { w: number; h: number } | null;
};

const labelMap: Record<string, string> = {
  last_name: "Last Name",
  first_name: "First Name",
  middle_name: "Middle",
  license_number: "License #",
  doc_number: "Document #",
  dob: "Date of Birth",
  issued: "Issued",
  expiry: "Expires",
  address_line1: "Address",
  city: "City",
  state: "State",
  postal_code: "ZIP",
  sex: "Sex",
  height_str: "Height",
  height_in: "Height (in)",
  eyes: "Eyes",
};

function formatAddress(s: StructuredFields) {
  const line1 = s.address_line1 || "";
  const line2 = [s.city, s.state, s.postal_code].filter(Boolean).join(", ").replace(", ", " ").replace("  ", " ");
  return [line1, line2].filter(Boolean).join("\n");
}

function hasAnyStructured(s?: StructuredFields | null) {
  if (!s) return false;
  const keys = ["first_name","last_name","dob","expiry","issued","license_number","doc_number","address_line1","city","state","postal_code"];
  return keys.some((k) => !!s[k]);
}

const App: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [preview, setPreview] = useState<string>("");
  const [structured, setStructured] = useState<StructuredFields | null>(null);
  const [feedback, setFeedback] = useState<CaptureFeedback | null>(null);
  const [showLive, setShowLive] = useState(false);

async function uploadFile() {
  if (!file) return;
  setBusy(true);
  setError(""); setNotice("");
  setText(""); setPreview("");
  setStructured(null as any); setFeedback(null as any);

  try {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch("/api/scan", { method: "POST", body: fd });

    const ct = res.headers.get("content-type") || "";
    const raw = await res.text();

    if (!res.ok) {
      if (ct.includes("application/json")) {
        const j = JSON.parse(raw);
        throw new Error(j.notice || j.error || `HTTP ${res.status}`);
      }
      throw new Error(`HTTP ${res.status}: ${raw.slice(0, 240)}`);
    }
    if (!ct.includes("application/json")) {
      throw new Error(`Unexpected response (not JSON): ${raw.slice(0, 240)}`);
    }

    const data = JSON.parse(raw);
    // States used by your UI
    setNotice(data.notice || "");
    setText(data.ocr_text || "");
    setPreview(data.preview_base64 || "");
    setStructured(data.structured || null);
    setFeedback(data.capture_feedback || null);

    if (data.error) setError(data.error); // e.g., ocr_engine_error
  } catch (e: any) {
    setError(e?.message || "Upload failed");
    console.error("Upload error:", e);
  } finally {
    setBusy(false);
  }
}

function renderStructuredRows(s: StructuredFields) {
  const hasAddress = !!s.address_line1 && (!!s.city || !!s.state || !!s.postal_code);

  const order = [
    "first_name",
    "middle_name",
    "last_name",
    "dob",
    "issued",
    "expiry",
    "license_number",
    "doc_number",
    "sex",
    "height_str",
    "height_in",
    "eyes",
    "address_line1", // Address row will include city/state/zip if present
    // "city", "state", "postal_code"  <-- we’ll conditionally skip these
  ];

  return order
    .filter((k) => s[k] !== undefined && s[k] !== null && String(s[k]).trim() !== "")
    .map((k) => {
      if (hasAddress && (k === "city" || k === "state" || k === "postal_code")) return null;
      return (
        <div key={k} style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, padding: "6px 0", borderBottom: "1px dashed #e5e7eb" }}>
          <div style={{ color: "#475569" }}>{labelMap[k] || k}</div>
          <div style={{ fontWeight: 500, whiteSpace: k === "address_line1" ? "pre-wrap" as const : "normal" }}>
            {k === "address_line1" ? formatAddress(s) : String(s[k])}
          </div>
        </div>
      );
    });
}

  function SourceBadge({ src }: { src?: string }) {
    const label = src === "barcode" ? "Barcode" : src === "mrz" ? "MRZ" : "OCR";
    const bg = src === "barcode" ? "#0ea5e9" : src === "mrz" ? "#8b5cf6" : "#64748b";
    return (
      <span style={{ background: bg, color: "white", borderRadius: 999, fontSize: 12, padding: "2px 8px", marginLeft: 8 }}>
        {label}
      </span>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 980, margin: "0 auto", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
      <h1 style={{ marginBottom: 12 }}>OCR — ID Scanner</h1>

      {/* Upload + Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <button
          onClick={uploadFile}
          disabled={!file || busy}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: !file || busy ? "#ddd" : "#2563eb",
            color: "white",
            cursor: !file || busy ? "default" : "pointer",
          }}
        >
          {busy ? "Scanning…" : "Scan File"}
        </button>

        <button
          onClick={() => setShowLive(true)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "#059669",
            color: "white",
            cursor: "pointer",
          }}
        >
          Live Scan
        </button>
      </div>

      {/* Alerts */}
      {error && <div style={{ color: "#dc2626", marginTop: 10 }}>{error}</div>}
      {notice && !error && <div style={{ color: "#334155", marginTop: 10 }}>{notice}</div>}

      {/* Capture tips */}
      {feedback?.suggestions?.length ? (
  <div style={{ marginTop: 12, background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 10, padding: 12 }}>
    <div style={{ fontWeight: 700, marginBottom: 6, color: "#9a3412" }}>Capture Tips</div>
    <ul style={{ margin: 0, paddingLeft: 18, color: "#7c2d12" }}>
      {feedback.suggestions.slice(0, 5).map((s, i) => (
        <li key={i} style={{ margin: "4px 0" }}>{s}</li>
      ))}
    </ul>
  </div>
) : null}

      {/* Processed Preview */}
      {preview && (
  <div style={{ position: "relative", marginTop: 16, display: "inline-block" }}>
    <img src={preview} alt="processed" style={{ maxWidth: 520, width: "100%", border: "1px solid #ddd", borderRadius: 6 }} />
    {/* Centered guide box */}
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{
        width: "72%", height: "46%",
        border: "2px dashed rgba(99,102,241,0.8)",
        borderRadius: 8
      }} />
    </div>
  </div>
)}

      {/* Parsed Fields */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: "8px 0" }}>Parsed Fields</h3>
          {structured?._source && <SourceBadge src={structured._source} />}
        </div>
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 12,
            background: "#ffffff",
          }}
        >
          {hasAnyStructured(structured) ? (
            <div>{renderStructuredRows(structured as StructuredFields)}</div>
          ) : (
            <div style={{ color: "#64748b" }}>— No structured fields detected —</div>
          )}
        </div>
      </div>

      {/* Raw OCR text */}
      <div style={{ marginTop: 16 }}>
        <h3 style={{ margin: "8px 0" }}>Extracted Text</h3>
        <pre
          style={{
            background: "#f6f8fa",
            padding: 12,
            whiteSpace: "pre-wrap",
            borderRadius: 6,
            minHeight: 80,
          }}
        >
          {text || "—"}
        </pre>
      </div>

      {/* Camera modal */}
      {showLive && (
        <CameraOCR
          apiScanUrl="/api/scan"
          apiGuideUrl="/api/guide"
          onClose={() => setShowLive(false)}
          onResult={(data: OCRScanResponse & ApiScanResponse) => {
            setText(data.ocr_text ?? "");
            setPreview(data.preview_base64 ?? "");
            setNotice(data.notice ?? "");
            setStructured((data as ApiScanResponse).structured ?? null);
            setFeedback((data as ApiScanResponse).capture_feedback ?? null);
            // keep modal open for rescan, or close if you prefer:
            // setShowLive(false);
          }}
        />
      )}
    </div>
  );
};

export default App;

